import { BIOME_IDS } from '../data/biomes.js';
import { AMBUSH_RARE_MATERIAL, AMBUSH_SHARE_RANGE, AMBUSH_XP_RAIDERS, ENCOUNTER_KINDS, ENCOUNTER_MAX_DISTANCE, ENCOUNTER_MIN_DISTANCE, ENCOUNTER_REACH, ENCOUNTER_VILLAGE_MARGIN, ENCOUNTERS, PEDDLER_LIMIT_PER_CHARACTER, PEDDLER_MARKUP, PEDDLER_WARES, } from '../data/encounters.js';
import { invaderOf } from '../data/event-mobs.js';
import { getItem } from '../data/items.js';
import { MOBS } from '../data/mobs.js';
import { UPGRADE_MATERIALS } from '../data/upgrade-materials.js';
import { rollChest } from './chests.js';
import { buyPrice } from './merchant.js';
/**
 * Road encounters (ADR 0093): where one may land, which kind, what the
 * peddler brings and asks, who a rescue pays and what a lost chest holds.
 * Pure: the server's `EncounterDirector` keeps the clock and the creatures.
 */
/** Packed earth: roads, lanes and trails (`world-terrain.ts`). */
const ROAD = 'd';
/** A zone's roads, as the rules read them: its ground, its biomes, its villages and its walls. */
export function roadMapOf(zone, grid) {
    return {
        width: zone.width,
        height: zone.height,
        ground: zone.ground ?? [],
        biomes: zone.biomes ?? [],
        villages: zone.landmarks.filter((place) => place.kind === 'village'),
        isWalkable: (x, y) => grid.isWalkable(x, y),
    };
}
function chebyshev(a, b) {
    return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}
/**
 * Every road tile an encounter may stand on, by biome: packed earth, open
 * on its four sides (room for the raiders round the traveller and for the
 * players to come up), and away from the villages.
 */
export function encounterSites(map) {
    const sites = new Map(BIOME_IDS.map((biome) => [biome, []]));
    for (let y = 1; y < map.height - 1; y += 1) {
        const row = map.ground[y];
        if (row === undefined)
            continue;
        for (let x = 1; x < map.width - 1; x += 1) {
            if (row[x] !== ROAD || !map.isWalkable(x, y))
                continue;
            if (!map.isWalkable(x + 1, y) ||
                !map.isWalkable(x - 1, y) ||
                !map.isWalkable(x, y + 1) ||
                !map.isWalkable(x, y - 1)) {
                continue;
            }
            const tile = { x, y };
            const inVillage = map.villages.some((village) => chebyshev(village, tile) <= village.radius + ENCOUNTER_VILLAGE_MARGIN);
            if (inVillage)
                continue;
            const biome = BIOME_IDS[Number(map.biomes[y]?.[x])];
            if (biome !== undefined)
                sites.get(biome)?.push(tile);
        }
    }
    return sites;
}
/** Which kind comes, by the weights of `ENCOUNTERS`. */
export function rollEncounterKind(rng) {
    const total = ENCOUNTER_KINDS.reduce((sum, kind) => sum + ENCOUNTERS[kind].weight, 0);
    let roll = rng() * total;
    for (const kind of ENCOUNTER_KINDS) {
        roll -= ENCOUNTERS[kind].weight;
        if (roll < 0)
            return kind;
    }
    return ENCOUNTER_KINDS[0];
}
/**
 * A site for the player it is drawn for: down the road from them, between
 * `ENCOUNTER_MIN_DISTANCE` and `ENCOUNTER_MAX_DISTANCE`, and never on
 * top of an encounter already on the roads. Undefined when none fits.
 */
export function pickEncounterSite(sites, near, taken, rng) {
    const fits = sites.filter((site) => {
        const distance = chebyshev(site, near);
        return (distance >= ENCOUNTER_MIN_DISTANCE &&
            distance <= ENCOUNTER_MAX_DISTANCE &&
            taken.every((other) => chebyshev(other, site) > ENCOUNTER_MAX_DISTANCE));
    });
    return fits[Math.floor(rng() * fits.length)];
}
/** Whether a player stands close enough to the peddler or the chest. */
export function withinEncounterReach(at, site) {
    return chebyshev(at, site) <= ENCOUNTER_REACH;
}
/** What the peddler asks for a lot: the merchant's buy price, marked up. */
export function peddlerPrice(templateId, quantity) {
    const template = getItem(templateId);
    if (template === undefined)
        throw new Error(`Unknown peddler ware "${templateId}"`);
    return Math.ceil(buyPrice(template) * PEDDLER_MARKUP) * quantity;
}
/** The one lot the peddler brings to this biome's road, drawn as he appears. */
export function rollPeddlerWare(biome, rng) {
    const wares = PEDDLER_WARES[biome];
    const total = wares.reduce((sum, ware) => sum + ware.weight, 0);
    let roll = rng() * total;
    const ware = wares.find((candidate) => (roll -= candidate.weight) < 0) ?? wares[0];
    if (ware === undefined)
        throw new Error(`No peddler wares for "${biome}"`);
    return {
        templateId: ware.templateId,
        quantity: ware.quantity,
        price: peddlerPrice(ware.templateId, ware.quantity),
    };
}
/**
 * Why a purchase is refused before the purse is opened, if it is: the
 * character already bought from this peddler, or stands too far. The gold
 * is checked again inside the transaction.
 */
export function peddlerRefusal(input) {
    if (!withinEncounterReach(input.at, input.site))
        return 'too-far';
    if (input.bought >= PEDDLER_LIMIT_PER_CHARACTER)
        return 'sold-out';
    if (input.gold < input.price)
        return 'missing-gold';
    return undefined;
}
/** The raiders who waylay a traveller in this biome: its invasion's. */
export function ambushRaider(biome) {
    return invaderOf(biome);
}
/** Each rescuer's experience: `AMBUSH_XP_RAIDERS` raiders' worth, whole for each. */
export function ambushRewardXp(biome) {
    return MOBS[ambushRaider(biome)].xp * AMBUSH_XP_RAIDERS;
}
/** What each rescuer finds at their feet: the biome's rare material. */
export function ambushRewardItems(biome) {
    return [{ templateId: UPGRADE_MATERIALS[biome].rare.id, quantity: AMBUSH_RARE_MATERIAL }];
}
/**
 * Who a rescue pays: every character who struck one of the raiders and is
 * still within `AMBUSH_SHARE_RANGE` of the traveller as the last falls.
 * Not split: each is paid whole, so helping a stranger costs nothing.
 */
export function ambushRescuers(strikers, positions, site) {
    return [...strikers].filter((characterId) => {
        const at = positions.get(characterId);
        return at !== undefined && chebyshev(at, site) <= AMBUSH_SHARE_RANGE;
    });
}
/** Whether a character may open this lost chest: near it, and never twice. */
export function lostChestRefusal(input) {
    if (!withinEncounterReach(input.at, input.site))
        return 'too-far';
    if (input.opened)
        return 'opened';
    return undefined;
}
/** What a lost chest holds: a hidden chest's fill (`rollChest`), dropped on the road. */
export function rollLostChest(biome, rng) {
    return rollChest(biome, 'hidden', rng);
}
//# sourceMappingURL=encounters.js.map